import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Cart, Step, ItemRef } from "../types/PricingCalculator";
import {
  pricingData,
  softwarePricing,
  exchangeRates,
} from "../data/PricingData";
import {
  priceSummary,
  canProceedToNextStep,
  getValidationMessage,
} from "../utils/pricingLogic";
import { decodeUrlToCart } from "../utils/urlEncoder";
import { findItemById } from "../utils/itemResolver";
import EligibilityStep from "./pricing/EligibilityStep";
import DeviceStrategyStep from "./pricing/DeviceStrategyStep";
import HardwareStep from "./pricing/HardwareStep";
import PlanStep from "./pricing/PlanStep";
import UserCountStep from "./pricing/UserCountStep";
import SummaryStep from "./pricing/SummaryStep";

const steps: { key: Step; title: string; description: string }[] = [
  {
    key: "eligibility",
    title: "pricingCalc.steps.eligibility.title",
    description: "pricingCalc.steps.eligibility.desc",
  },
  {
    key: "device-strategy",
    title: "pricingCalc.steps.device.title",
    description: "pricingCalc.steps.device.desc",
  },
  {
    key: "hardware",
    title: "pricingCalc.steps.hardware.title",
    description: "pricingCalc.steps.hardware.desc",
  },
  {
    key: "plan",
    title: "pricingCalc.steps.plan.title",
    description: "pricingCalc.steps.plan.desc",
  },
  {
    key: "user-count",
    title: "pricingCalc.steps.users.title",
    description: "pricingCalc.steps.users.desc",
  },
  {
    key: "summary",
    title: "pricingCalc.steps.summary.title",
    description: "pricingCalc.steps.summary.desc",
  },
];

const PricingCalculator: React.FC = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<Step>("eligibility");

  // Initialize cart with URL parameters if available
  const initializeCart = (): Cart => {
    const urlConfig = decodeUrlToCart(window.location.href);
    if (urlConfig) {
      // Restore from URL parameters
      return {
        eligibility: {
          rrdbRegistrationDateISO:
            urlConfig.eligibility?.rrdbRegistrationDateISO || "",
          isFree: urlConfig.eligibility?.isFree || false,
        },
        strategy: urlConfig.strategy || {
          existingSmartphone: false,
        },
        selections: {
          screen: urlConfig.selections?.screen
            ? findItemById(urlConfig.selections.screen) || undefined
            : undefined,
          printer: urlConfig.selections?.printer
            ? findItemById(urlConfig.selections.printer) || undefined
            : undefined,
          scanner: urlConfig.selections?.scanner
            ? findItemById(urlConfig.selections.scanner) || undefined
            : undefined,
          adapters:
            (urlConfig.selections?.adapters
              ?.map((id) => findItemById(id))
              .filter(Boolean) as ItemRef[]) || [],
          software: {
            id: "software-hovastore",
            name: softwarePricing.name,
            description: softwarePricing.description,
            category: "software",
            price: softwarePricing.basePrice,
            availability: "Rwanda",
            link: "#",
          },
        },
        termMonths: urlConfig.termMonths || 12,
        userCount: urlConfig.userCount || 2,
        fx: exchangeRates,
        totals: {
          hardware: { value: 0, currency: "RWF" },
          software: { value: 0, currency: "RWF" },
          additionalUsers: { value: 0, currency: "RWF" },
          grandTotal: { value: 0, currency: "RWF" },
        },
      };
    }

    // Default cart
    return {
      eligibility: {
        rrdbRegistrationDateISO: "",
        isFree: false,
      },
      strategy: {
        existingSmartphone: false,
      },
      selections: {
        software: {
          id: "software-hovastore",
          name: softwarePricing.name,
          description: softwarePricing.description,
          category: "software",
          price: softwarePricing.basePrice,
          availability: "Rwanda",
          link: "#",
        },
      },
      termMonths: 12,
      userCount: 2,
      fx: exchangeRates,
      totals: {
        hardware: { value: 0, currency: "RWF" },
        software: { value: 0, currency: "RWF" },
        additionalUsers: { value: 0, currency: "RWF" },
        grandTotal: { value: 0, currency: "RWF" },
      },
    };
  };

  const [cart, setCart] = useState<Cart>(initializeCart());

  // Update totals whenever cart changes
  useEffect(() => {
    setCart((prev) => {
      const newTotals = priceSummary(prev);
      return {
        ...prev,
        totals: newTotals,
      };
    });
  }, [
    cart.eligibility,
    cart.strategy,
    cart.selections,
    cart.termMonths,
    cart.userCount,
  ]);

  // Handle URL parameters on component mount
  useEffect(() => {
    const urlConfig = decodeUrlToCart(window.location.href);
    if (urlConfig) {
      // If we have a shared configuration, jump to summary step
      setCurrentStep("summary");
    }
  }, []);

  const handleNext = () => {
    const currentIndex = steps.findIndex((step) => step.key === currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].key);
    }
  };

  const handlePrevious = () => {
    const currentIndex = steps.findIndex((step) => step.key === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].key);
    }
  };

  const handleStepClick = (step: string) => {
    setCurrentStep(step as Step);
  };

  const canProceed = canProceedToNextStep(currentStep, cart);

  const renderCurrentStep = () => {
    switch (currentStep) {
      case "eligibility":
        return (
          <EligibilityStep
            cart={cart}
            onUpdate={(updates: Partial<Cart>) =>
              setCart((prev) => ({ ...prev, ...updates }))
            }
          />
        );
      case "device-strategy":
        return (
          <DeviceStrategyStep
            cart={cart}
            onUpdate={(updates: Partial<Cart>) =>
              setCart((prev) => ({ ...prev, ...updates }))
            }
          />
        );
      case "hardware":
        return (
          <HardwareStep
            cart={cart}
            pricingData={pricingData}
            onUpdate={(updates: Partial<Cart>) =>
              setCart((prev) => ({ ...prev, ...updates }))
            }
          />
        );
      case "plan":
        return (
          <PlanStep
            cart={cart}
            onUpdate={(updates: Partial<Cart>) =>
              setCart((prev) => ({ ...prev, ...updates }))
            }
          />
        );
      case "user-count":
        return (
          <UserCountStep
            cart={cart}
            onUpdate={(updates: Partial<Cart>) =>
              setCart((prev) => ({ ...prev, ...updates }))
            }
          />
        );
      case "summary":
        return <SummaryStep cart={cart} onEditStep={handleStepClick} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {t("pricingCalc.headerTitle")}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            {t("pricingCalc.headerSubtitle")}
          </p>
          <div className="mt-4 mx-auto max-w-3xl">
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
              {t("pricingCalc.headerNotice")} {t("pricingCalc.headerNoticeApp")}
              :{" "}
              <a
                href="https://play.google.com/store/apps/details?id=ai.hova.hovastore"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium hover:text-blue-900 dark:hover:text-blue-300"
              >
                HovaStore on Google Play
              </a>
              .
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const isActive = step.key === currentStep;
              const isCompleted =
                steps.findIndex((s) => s.key === currentStep) > index;
              const isClickable = isCompleted || step.key === currentStep;

              return (
                <div
                  key={step.key}
                  className="flex flex-col items-center flex-1"
                >
                  <button
                    onClick={() => isClickable && handleStepClick(step.key)}
                    disabled={!isClickable}
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : isCompleted
                        ? "bg-green-500 text-white"
                        : "bg-gray-300 text-gray-500"
                    } ${
                      isClickable
                        ? "cursor-pointer hover:bg-opacity-80"
                        : "cursor-not-allowed"
                    }`}
                  >
                    {isCompleted ? "✓" : index + 1}
                  </button>
                  <div className="mt-2 text-center">
                    <div
                      className={`text-sm font-medium ${
                        isActive
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-500 dark:text-gray-200"
                      }`}
                    >
                      {t(step.title)}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-400 mt-1">
                      {t(step.description)}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`absolute top-5 left-1/2 w-full h-0.5 -z-10 ${
                        isCompleted
                          ? "bg-green-500"
                          : "bg-gray-300 dark:bg-gray-700"
                      }`}
                      style={{ left: "50%", width: "calc(100% - 2.5rem)" }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white dark:bg-gray-800 dark:border dark:border-gray-700 rounded-lg shadow-lg p-6 mb-8">
          {renderCurrentStep()}
        </div>

        {/* Validation Message */}
        {!canProceed && currentStep !== "summary" && (
          <div className="flex justify-center pt-4">
            <div className="bg-yellow-50 dark:bg-amber-900/30 border border-yellow-200 dark:border-amber-800 rounded-lg p-4 max-w-md">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-yellow-600 dark:text-amber-300 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <p className="text-yellow-800 text-sm">
                  {t(getValidationMessage(currentStep, cart))}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === "eligibility"}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("pricingCalc.nav.previous")}
          </button>

          {currentStep !== "summary" && (
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("pricingCalc.nav.next")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;
